import { useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { eur } from "../lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";

export default function CartDrawer() {
  const { items, open, setOpen, setQuantity, removeItem, total } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        className="bg-[#121214] border-l border-white/10 text-foreground w-full sm:max-w-md p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-8 pt-8 pb-6 border-b border-white/10">
          <SheetTitle className="font-serif text-2xl font-light text-foreground">
            Il tuo carrello
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
          {items.length === 0 && (
            <p className="text-muted-foreground text-sm tracking-wide" data-testid="cart-empty-message">
              Il carrello è vuoto. La collezione ti aspetta.
            </p>
          )}
          <AnimatePresence initial={false}>
            {items.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 24 }}
                transition={{ duration: 0.35 }}
                className="flex gap-5"
                data-testid={`cart-item-${item.id}`}
              >
                <div className="w-20 h-24 overflow-hidden flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-serif text-lg leading-tight">{item.name}</p>
                    <button
                      data-testid={`cart-remove-${item.id}`}
                      onClick={() => removeItem(item.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      aria-label={`Rimuovi ${item.name}`}
                    >
                      <X size={15} />
                    </button>
                  </div>
                  <p className="text-primary text-sm mt-1">{eur(item.price)}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <button
                      data-testid={`cart-decrease-${item.id}`}
                      onClick={() => setQuantity(item.id, item.quantity - 1)}
                      className="h-7 w-7 border border-white/15 flex items-center justify-center hover:border-primary transition-colors"
                      aria-label="Diminuisci quantità"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="text-sm w-4 text-center" data-testid={`cart-qty-${item.id}`}>
                      {item.quantity}
                    </span>
                    <button
                      data-testid={`cart-increase-${item.id}`}
                      onClick={() => setQuantity(item.id, item.quantity + 1)}
                      className="h-7 w-7 border border-white/15 flex items-center justify-center hover:border-primary transition-colors"
                      aria-label="Aumenta quantità"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {items.length > 0 && (
          <div className="px-8 py-6 border-t border-white/10 space-y-5">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Subtotale
              </span>
              <span className="font-serif text-2xl" data-testid="cart-subtotal">
                {eur(total)}
              </span>
            </div>
            <button
              data-testid="cart-checkout-button"
              onClick={() => {
                setOpen(false);
                navigate("/checkout");
              }}
              className="w-full h-13 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300"
            >
              Procedi al checkout
            </button>
            <p className="text-[10px] text-muted-foreground/60 tracking-wide text-center" data-testid="cart-vendor">
              Venditore: AB fëlise shop - P.IVA 03383950809
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
